import React, { useState, useMemo, useEffect, useRef, useCallback } from 'react';
import PropTypes from 'prop-types';
import { useSelector } from 'react-redux';

import { Link } from 'react-router-dom';
import SplashBall from '../../../Splash/splashBall';
import PopOverMenu from '../../../PopOverMenu';
import SwitchButton from '../../../Form/ToggleSwitch/SwitchButton';
import PopupV3 from '../../../Popup/PopupV3';
import Img from '../../../Img';
import MobileModal from '../../../../pages/SearchPage/SearchV2/SharedComponents/MobileModal';
import ActivityStatCard from './ActivityStatCard';
import { trackEvent } from '../../../Analytics/TrackedClick';
import {
    profileAnalyticsEvents,
    activityStatsAnalyticsEvents,
    getAnalyticsCommonTraits,
    getActivityStatsTraits,
} from '../../../../utils/analytics';
import { getFilteredStats, timeframeToAnalyticsValue, resolveDefaultTimeFrame } from './activityStatsUtils';
import paths from '../../../../../constants/paths';
import links from '../../../../../config/links';
import useStartPowerCheckout from '../../../hooks/useStartPowerCheckout';
import { PowerFunnelSurfaces, PowerFunnelGatedFeatures } from '../../../Analytics/constants';
import EventsNearMe from '../../../../pages/DashboardPage/DashboardContentV2/EventsNearMe';

import {
    activityTimeOptions,
    activityGameTypeOptions,
    activityTennisStatKeys,
    activityPickleballStatKeys,
    activityDefaultTennisFilters,
    activityDefaultPickleballFilters,
} from '../../../../utils/constants';

import styles from './activityStats.scss';

function renderTennisInfoContent(rankLabel) {
    return (
        <div className={styles.infoContent}>
            <p>Track your tennis progress with a detailed look at your playing history and recent performance:</p>
            <ul>
                <li>
                    <strong>Total Volume</strong><br />
                    View your all-time Matches, Sets, and Games Played to measure your overall experience on court.
                </li>
                <li>
                    <strong>Performance Under Pressure</strong>
                    <ul>
                        <li>Decisive Set Matches: See how you perform when a match goes the distance (e.g., a 3rd set tiebreak or full set).</li>
                        <li>
                            Tiebreaker Sets: Track your win/loss ratio in sets decided by a 7-point or 10-point tiebreaker.
                        </li>
                    </ul>
                </li>
                <li>
                    <strong>Efficiency</strong><br />
                    Straight Set Matches shows your ability to close out opponents without dropping a set.
                </li>
                <li>
                    <strong>Peer Ranking</strong><br />
                    {`Your rank is calculated relative to other players in the ${rankLabel} division to show how you stack up against your most frequent competition.`}
                </li>
            </ul>
        </div>
    );
}

function renderPickleballInfoContent(rankLabel) {
    return (
        <div className={styles.infoContent}>
            <p>Track your wins, losses, and rankings across different formats.</p>
            <ul>
                <li>
                    <strong>Total Volume:</strong><br />
                    See your total Matches and Games played.
                </li>
                <li>
                    <strong>Point Spread</strong><br />
                    Evaluate your scoring efficiency.
                </li>
                <li>
                    <strong>Peer Ranking</strong><br />
                    {`See where you stand in the ${rankLabel} division.`}
                </li>
                <li>
                    <strong>Match Type</strong><br />
                    Compare your success in single-game sprints versus multi-game endurance matches.
                </li>
            </ul>
        </div>
    );
}

function SportSection({
    sportKey,
    sportLabel,
    badgeClass,
    allData,
    filters,
    setFilters,
    fallbackStatKeys,
    timeframeOptions,
    launchPaywall,
    auth,
    playerId,
    trackCtaClick,
    isOwnProfile,
    missingAgeGroup,
}) {
    const [gameTypeModalOpen, setGameTypeModalOpen] = useState(false);
    const [timeFrameModalOpen, setTimeFrameModalOpen] = useState(false);
    const [tempGameType, setTempGameType] = useState(filters.matchType);
    const [tempTimeFrame, setTempTimeFrame] = useState(filters.timeFrame);

    const stats = getFilteredStats(allData, sportKey, filters, fallbackStatKeys);

    if (!allData?.sports?.[sportKey]) { return null; }
    // A match type counts as empty only when the player has unlocked metrics and every one of
    // them is zero — real, tracked activity that happens to have no results. When every metric
    // is locked (a free user), `every` would be vacuously true, so the `length > 0` guard keeps
    // the section showing teaser cards instead of the empty message.
    const unlockedStats = stats.filter(s => !s.locked);
    const isEmptyForFilters = unlockedStats.length > 0 && unlockedStats.every(s => s.total === 0);

    const activityTraits = getActivityStatsTraits(auth, playerId, isOwnProfile);

    const handleGameTypeChange = (item) => {
        setFilters({ ...filters, matchType: item.value });
        trackEvent(activityStatsAnalyticsEvents.GAME_TYPE_SELECTED, {
            ...activityTraits,
            game_type: String(item.value).toLowerCase(),
        });
    };

    const handleVerifiedChange = () => {
        const newVerified = !filters.verified;
        setFilters({ ...filters, verified: newVerified });
        trackEvent(activityStatsAnalyticsEvents.VERIFIED_ONLY_TOGGLED, {
            ...activityTraits,
            verified_only: newVerified,
        });
    };

    const handleTimeChange = (timeValue) => {
        setFilters({ ...filters, timeFrame: timeValue });
        trackEvent(activityStatsAnalyticsEvents.TIMEFRAME_SELECTED, {
            ...activityTraits,
            timeframe: timeframeToAnalyticsValue(timeValue),
        });
    };

    const handleFindEventsCtaClick = () => {
        trackEvent(activityStatsAnalyticsEvents.FIND_EVENTS_CTA_TAPPED, {
            ...activityTraits,
            game_type: String(filters.matchType).toLowerCase(),
            timeframe: timeframeToAnalyticsValue(filters.timeFrame),
            verified_only: filters.verified,
        });
    };

    const handleMobileGameTypeApply = () => {
        handleGameTypeChange({ value: tempGameType });
        setGameTypeModalOpen(false);
    };

    const handleMobileTimeFrameApply = () => {
        handleTimeChange(tempTimeFrame);
        setTimeFrameModalOpen(false);
    };

    const sportEventLabel = sportKey === 'tennis' ? 'tennis' : 'pickleball';

    return (
        <div className={styles.sportSection} data-testid={`profile.activity-stats.${sportKey}`}>
            <span className={`${styles.sportBadge} ${badgeClass}`} data-testid={`profile.activity-stats.${sportKey}.badge`}>{sportLabel}</span>
            <div className={styles.sportSectionContent}>
                <div className={styles.sportSectionHeader}>
                    <div className={styles.filterBar}>
                        <div className={styles.filterBarDesktop}>
                            <div className={styles.gameTypeMenuWrapper}>
                                <PopOverMenu
                                    menuItems={activityGameTypeOptions}
                                    onSelected={handleGameTypeChange}
                                    alignLeft
                                    data-testid={`profile.activity-stats.${sportKey}.game-type-menu`}
                                >
                                    <span className={styles.gameTypeAnchor}>{filters.matchType}<span className={`utr-icon icon-chevron-down ${styles.dropdownChevron}`} /></span>
                                </PopOverMenu>
                            </div>
                            <div className={styles.verifiedToggle}>
                                <SwitchButton
                                    id={`verified-${sportKey}`}
                                    name={`verified-${sportKey}`}
                                    checked={filters.verified}
                                    onChange={handleVerifiedChange}
                                    label=""
                                    labelRight="Verified Matches Only"
                                    data-testid={`profile.activity-stats.${sportKey}.verified-toggle`}
                                />
                            </div>
                        </div>
                        <div className={styles.filterBarMobile}>
                            <div className={styles.mobileFilterRow}>
                                <button
                                    type="button"
                                    className={styles.mobileDropdownBtn}
                                    onClick={() => { setTempGameType(filters.matchType); setGameTypeModalOpen(true); }}
                                    data-testid={`profile.activity-stats.${sportKey}.mobile.game-type-btn`}
                                >
                                    <span>{filters.matchType}</span>
                                    <span className={`utr-icon icon-chevron-down ${styles.dropdownChevron}`} />
                                </button>
                                <button
                                    type="button"
                                    className={styles.mobileDropdownBtn}
                                    onClick={() => { setTempTimeFrame(filters.timeFrame); setTimeFrameModalOpen(true); }}
                                    data-testid={`profile.activity-stats.${sportKey}.mobile.timeframe-btn`}
                                >
                                    <span>{timeframeOptions.find(o => o.value === filters.timeFrame)?.label || 'All Time'}</span>
                                    <i className="uts uts-expand-arrow" />
                                </button>
                            </div>
                            <div className={styles.mobileVerifiedRow}>
                                <SwitchButton
                                    id={`mobile-verified-${sportKey}`}
                                    name={`mobile-verified-${sportKey}`}
                                    checked={filters.verified}
                                    onChange={handleVerifiedChange}
                                    label=""
                                    labelRight="Verified Matches Only"
                                    data-testid={`profile.activity-stats.${sportKey}.mobile.verified-toggle`}
                                />
                            </div>
                        </div>
                    </div>
                    <div className={styles.timePills}>
                        {timeframeOptions.map(opt => (opt.locked ? (
                            <button
                                key={opt.value}
                                type="button"
                                className={`${styles.timePill} ${styles.timePillLocked}`}
                                onClick={launchPaywall}
                                data-testid={`profile.activity-stats.${sportKey}.time-pill-${opt.value}`}
                            >
                                {opt.label}
                            </button>
                        ) : (
                            <button
                                key={opt.value}
                                type="button"
                                className={`${styles.timePill} ${filters.timeFrame === opt.value ? styles.timePillActive : ''}`}
                                onClick={() => handleTimeChange(opt.value)}
                                data-testid={`profile.activity-stats.${sportKey}.time-pill-${opt.value}`}
                            >
                                {opt.label}
                            </button>
                        )))}
                    </div>
                </div>
                {isEmptyForFilters ? (
                    <div className={styles.sportSectionEmptyState} data-testid={`profile.activity-stats.${sportKey}.empty-state`}>
                        <p className={styles.sportSectionEmptyText} data-testid={`profile.activity-stats.${sportKey}.empty-state-text`}>
                            There are no activity stats for this timeframe.
                        </p>
                    </div>
                ) : (
                    <React.Fragment>
                        <div className={styles.statCardGrid}>
                            {stats.map(stat => (
                                <ActivityStatCard
                                    key={stat.statKey}
                                    title={stat.title}
                                    mobileTitle={stat.mobileTitle}
                                    total={stat.total}
                                    wins={stat.wins}
                                    losses={stat.losses}
                                    rank={stat.rank}
                                    rankDisplay={stat.rankDisplay}
                                    rankLabel={stat.rankLabel}
                                    locked={stat.locked}
                                    onLockedClick={launchPaywall}
                                    data-testid={`profile.activity-stats.${sportKey}.metric-${stat.statKey}`}
                                />
                            ))}
                        </div>
                        {isOwnProfile && (
                            <div className={styles.sportCtaGroup}>
                                {missingAgeGroup && (
                                    <div className={styles.contactSupportRow}>
                                        {'Contact '}
                                        <a
                                            href={links.support}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            className={styles.contactSupportLink}
                                            onClick={() => trackCtaClick('Contact Customer Support', links.support)}
                                            data-testid={`profile.activity-stats.${sportKey}.contact-support-link`}
                                        >
                                            Customer Support
                                        </a>
                                        {' to update your date of birth in order to see your rankings in your age group.'}
                                    </div>
                                )}
                                <div className={styles.sportCta}>
                                    <div>Improve by playing more often!</div>
                                    <div>
                                        <Link to={`/search?type=events&sportTypes=${sportKey}`} className={styles.sportCtaLink} onClick={handleFindEventsCtaClick} data-testid={`profile.activity-stats.${sportKey}.find-events-link`}>Click here</Link>
                                        {` to find ${sportEventLabel} events.`}
                                    </div>
                                </div>
                            </div>
                        )}
                    </React.Fragment>
                )}
            </div>
            {typeof document !== 'undefined' && (
                <MobileModal
                    isOpen={gameTypeModalOpen}
                    onClose={() => setGameTypeModalOpen(false)}
                    title="Filter Game Type"
                    onApply={handleMobileGameTypeApply}
                    showCancelButton={false}
                >
                    <div className={styles.mobileRadioList}>
                        {activityGameTypeOptions.map(opt => (
                            <label key={opt.value} className={styles.mobileRadioItem}>
                                <input
                                    type="radio"
                                    name={`gameType-${sportKey}`}
                                    checked={tempGameType === opt.value}
                                    onChange={() => setTempGameType(opt.value)}
                                />
                                <span>{opt.label}</span>
                            </label>
                        ))}
                    </div>
                </MobileModal>
            )}
            {typeof document !== 'undefined' && (
                <MobileModal
                    isOpen={timeFrameModalOpen}
                    onClose={() => setTimeFrameModalOpen(false)}
                    title="Filter Timeframe"
                    onApply={handleMobileTimeFrameApply}
                    showCancelButton={false}
                >
                    <div className={styles.mobileRadioList}>
                        {timeframeOptions.map(opt => (
                            <label
                                key={opt.value}
                                className={`${styles.mobileRadioItem} ${opt.locked ? styles.mobileRadioItemLocked : ''}`}
                                data-testid={`modal.activity-timeframe.${sportKey}.option-${opt.value}`}
                            >
                                <input
                                    type="radio"
                                    name={`timeFrame-${sportKey}`}
                                    checked={tempTimeFrame === opt.value}
                                    onChange={() => { if (opt.locked) { launchPaywall(); } else { setTempTimeFrame(opt.value); } }}
                                    data-testid={`modal.activity-timeframe.${sportKey}.option-${opt.value}.radio`}
                                />
                                <span data-testid={`modal.activity-timeframe.${sportKey}.option-${opt.value}.label`}>{opt.label}</span>
                            </label>
                        ))}
                    </div>
                </MobileModal>
            )}
        </div>
    );
}

SportSection.propTypes = {
    sportKey: PropTypes.string.isRequired,
    sportLabel: PropTypes.string.isRequired,
    badgeClass: PropTypes.string.isRequired,
    allData: PropTypes.shape({}),
    filters: PropTypes.shape({}).isRequired,
    setFilters: PropTypes.func.isRequired,
    fallbackStatKeys: PropTypes.arrayOf(PropTypes.shape({})).isRequired,
    timeframeOptions: PropTypes.arrayOf(PropTypes.shape({
        value: PropTypes.string,
        label: PropTypes.string,
        locked: PropTypes.bool,
    })).isRequired,
    launchPaywall: PropTypes.func.isRequired,
    auth: PropTypes.shape({}),
    playerId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    trackCtaClick: PropTypes.func.isRequired,
    isOwnProfile: PropTypes.bool,
    missingAgeGroup: PropTypes.bool,
};

SportSection.defaultProps = {
    allData: null,
    auth: null,
    playerId: null,
    isOwnProfile: false,
    missingAgeGroup: false,
};

function ActivityStats({ auth, isOwnProfile, playerId }) {
    const { volumetricStats } = useSelector(({ profile }) => profile);
    const [tennisFilters, setTennisFilters] = useState({ ...activityDefaultTennisFilters });
    const [pickleballFilters, setPickleballFilters] = useState({ ...activityDefaultPickleballFilters });
    const [showDesktopInfo, setShowDesktopInfo] = useState(false);
    const [showInfoModal, setShowInfoModal] = useState(false);
    const { startPowerCheckout } = useStartPowerCheckout();

    const { isLoading, data: allData, error } = volumetricStats;

    const hasAppliedDefaultTimeFrame = useRef(false);
    const recommendedTimeFrame = allData?.recommendedTimeFrame;
    const timeframes = allData?.timeframes;

    useEffect(() => {
        if (!allData) {
            hasAppliedDefaultTimeFrame.current = false;
            return;
        }
        if (hasAppliedDefaultTimeFrame.current) { return; }
        const defaultTimeFrame = resolveDefaultTimeFrame(recommendedTimeFrame, timeframes);
        if (!defaultTimeFrame) { return; }
        setTennisFilters(prev => ({ ...prev, timeFrame: defaultTimeFrame }));
        setPickleballFilters(prev => ({ ...prev, timeFrame: defaultTimeFrame }));
        hasAppliedDefaultTimeFrame.current = true;
    }, [allData, recommendedTimeFrame, timeframes]);

    const filterPreferences = useMemo(() => {
        if (!auth?.user?.location?.latLng) { return null; }
        return {
            lat: auth.user.location.latLng[0],
            lng: auth.user.location.latLng[1],
            location: auth.user.location.display,
            distance: '200mi',
            sportTypes: { tennis: true, pickleball: true },
        };
    }, [auth]);

    const launchPaywall = useCallback(() => {
        startPowerCheckout({
            surface: PowerFunnelSurfaces.PLAYER_PROFILE,
            gatedFeature: PowerFunnelGatedFeatures.ACTIVITY_STATS,
        });
    }, [startPowerCheckout]);

    // Assumes a single timeframe list (including locked state) shared across all sports:
    // both SportSection instances below receive this same array. If the API ever returns
    // sport-specific timeframe locks, derive a per-sport options list here instead.
    const timeframeOptions = useMemo(() => (
        Array.isArray(allData?.timeframes) && allData.timeframes.length > 0
            ? allData.timeframes.map(t => ({ value: t.key, label: t.display, locked: !!t.locked }))
            : activityTimeOptions.map(o => ({ ...o, locked: false }))
    ), [allData]);

    if (isLoading) {
        return (
            <div className={styles.activityStatsContainer} data-testid="profile.activity-stats">
                <div className={styles.activityStatsLoading} data-testid="profile.activity-stats.loading">
                    <SplashBall />
                </div>
            </div>
        );
    }

    if (error) {
        return (
            <div className={styles.activityStatsContainer} data-testid="profile.activity-stats">
                <div className={styles.activityStatsError} data-testid="profile.activity-stats.error">
                    Unable to retrieve activity stats
                </div>
            </div>
        );
    }

    const commonTraits = getAnalyticsCommonTraits(auth);

    const trackCtaClick = (ctaLabel, ctaUrl) => {
        trackEvent(profileAnalyticsEvents.EVENT_CTA_CLICKED, {
            ctaLabel, ctaUrl, section: 'activity', ...commonTraits,
        });
    };

    const hasTennis = !!allData?.sports?.tennis;
    const hasPickleball = !!allData?.sports?.pickleball;
    const hasBothSports = hasTennis && hasPickleball;
    const hasAnyData = hasTennis || hasPickleball;
    const missingAgeGroup = !!allData && (allData.ageGroup == null || allData.ageGroupDisplay == null);

    const infoRankLabel =
        allData?.rankLabel ||
        (allData?.ageGroup ? `Ages ${allData.ageGroup}` : 'Age Group');

    const renderInfoContent = () => {
        if (hasBothSports) {
            return (
                <div className={styles.infoTwoColumn}>
                    <div className={styles.infoColumn}>
                        <div className={styles.infoSportHeader}>Tennis</div>
                        {renderTennisInfoContent(infoRankLabel)}
                    </div>
                    <div className={styles.infoColumnDivider} />
                    <div className={styles.infoColumn}>
                        <div className={styles.infoSportHeader}>Pickleball</div>
                        {renderPickleballInfoContent(infoRankLabel)}
                    </div>
                </div>
            );
        }
        if (hasPickleball) {
            return renderPickleballInfoContent(infoRankLabel);
        }
        return renderTennisInfoContent(infoRankLabel);
    };

    const renderDesktopInfoPopup = () => {
        const popupClass = hasBothSports
            ? `${styles.desktopInfoPopup} ${styles.desktopInfoPopupWide}`
            : styles.desktopInfoPopup;

        return (
            <PopupV3
                isOpen={showDesktopInfo}
                onCancelClick={() => setShowDesktopInfo(false)}
                noIcon
                showConfirmButton={false}
                showCancelButton={false}
                className={popupClass}
                message={(
                    <React.Fragment>
                        <div className={styles.desktopInfoPopupTitle}>About Activity History</div>
                        {renderInfoContent()}
                        <div className={styles.desktopInfoPopupFooter}>
                            <button
                                type="button"
                                className={styles.desktopInfoCloseBtn}
                                onClick={() => setShowDesktopInfo(false)}
                                data-testid="profile.activity-stats.info-popup.close-btn"
                            >
                                CLOSE
                            </button>
                        </div>
                    </React.Fragment>
                )}
            />
        );
    };

    const renderInfoModal = () => {
        if (typeof document === 'undefined') { return null; }
        return (
            <MobileModal
                isOpen={showInfoModal}
                onClose={() => setShowInfoModal(false)}
                title="About Activity History"
                titleClassName={styles.infoModalTitle}
                showFooter={false}
            >
                {renderInfoContent()}
            </MobileModal>
        );
    };

    return (
        <div className={styles.activityStatsContainer} data-testid="profile.activity-stats">
            <div className="container">
                <div className={styles.header}>
                    <h3 className={styles.headerTitle} data-testid="profile.activity-stats.title">Activity History</h3>
                    <div className={styles.desktopInfoButton}>
                        <div
                            className={styles.infoIconTrigger}
                            onClick={() => setShowDesktopInfo(true)}
                            data-testid="profile.activity-stats.info-btn"
                        >
                            <Img src="/img/info-icon.svg" alt="info" className={styles.infoIcon} />
                        </div>
                    </div>
                    <div
                        className={styles.mobileInfoButton}
                        onClick={() => setShowInfoModal(true)}
                        data-testid="profile.activity-stats.mobile-info-btn"
                    >
                        <Img src="/img/info-icon.svg" alt="info" className={styles.infoIcon} />
                    </div>
                </div>

                {renderDesktopInfoPopup()}
                {renderInfoModal()}

                {!hasAnyData && (
                    <div className={styles.activityEmptyState} data-testid="profile.activity-stats.empty-state">
                        <p className={styles.activityEmptyStateText} data-testid="profile.activity-stats.empty-state.text">
                            There is no activity history.
                        </p>
                        {isOwnProfile && (
                            <div className={styles.emptyStateLinks}>
                                <Link to={paths.searchEvents} className={styles.emptyStateLink} onClick={() => trackCtaClick('Find Events', paths.searchEvents)} data-testid="profile.activity-stats.empty-state.find-events-link">Find Events</Link>
                                <span className={styles.emptyStateDivider} />
                                <Link to={paths.searchPlayers} className={styles.emptyStateLink} onClick={() => trackCtaClick('Find Players', paths.searchPlayers)} data-testid="profile.activity-stats.empty-state.find-players-link">Find Players</Link>
                                <span className={styles.emptyStateDivider} />
                                <Link to={paths.postScoreSelect} className={styles.emptyStateLink} onClick={() => trackCtaClick('Post Scores', paths.postScoreSelect)} data-testid="profile.activity-stats.empty-state.post-scores-link">Post Scores</Link>
                            </div>
                        )}
                    </div>
                )}

                {!hasAnyData && isOwnProfile && filterPreferences && (
                    <EventsNearMe filterPreferences={filterPreferences} />
                )}

                {hasTennis && (
                    <SportSection
                        sportKey="tennis"
                        sportLabel="Tennis"
                        badgeClass={styles.sportBadgeTennis}
                        allData={allData}
                        filters={tennisFilters}
                        setFilters={setTennisFilters}
                        fallbackStatKeys={activityTennisStatKeys}
                        timeframeOptions={timeframeOptions}
                        launchPaywall={launchPaywall}
                        auth={auth}
                        playerId={playerId}
                        trackCtaClick={trackCtaClick}
                        isOwnProfile={isOwnProfile}
                        missingAgeGroup={missingAgeGroup}
                    />
                )}

                {hasPickleball && (
                    <SportSection
                        sportKey="pickleball"
                        sportLabel="Pickleball"
                        badgeClass={styles.sportBadgePickleball}
                        allData={allData}
                        filters={pickleballFilters}
                        setFilters={setPickleballFilters}
                        fallbackStatKeys={activityPickleballStatKeys}
                        timeframeOptions={timeframeOptions}
                        launchPaywall={launchPaywall}
                        auth={auth}
                        playerId={playerId}
                        trackCtaClick={trackCtaClick}
                        isOwnProfile={isOwnProfile}
                        missingAgeGroup={missingAgeGroup}
                    />
                )}
            </div>
        </div>
    );
}

ActivityStats.propTypes = {
    auth: PropTypes.shape({}),
    isOwnProfile: PropTypes.bool,
    playerId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
};

ActivityStats.defaultProps = {
    auth: null,
    isOwnProfile: false,
    playerId: null,
};

export default ActivityStats;
